import React, { useEffect, useState, useRef, useCallback, forwardRef } from 'react';
import '../../styles/AllBoatsSection.css';
import BoatService from '../../services/BoatService';
import BoatCard from '../BoatsPage/BoatCard';
import BoatListFilter from './BoatListFilter'
import useInView from './useInView';
import BoatSizeFilterModal from './BoatSizeFilterModal';
import BoatCategoryFilterModal from './BoatCategoryFilterModal';
import useBoatFiltersFromUrl from './useBoatFiltersFromUrl';

const BoatCardFadeInWrapper = forwardRef(({ children, index, ...props }, ref) => {
  const [viewRef, inView] = useInView({ threshold: 0.15 });
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  
  // Los primeros 3 botes (primera fila) aparecen inmediatamente
  const isFirstRow = index < 3;
  
  // Una vez que se hace visible, mantenerlo así
  useEffect(() => {
    if (inView && !hasBeenVisible) {
      setHasBeenVisible(true);
    }
  }, [inView, hasBeenVisible]);
  
  return (
    <div
      ref={(node) => {
        // Asignar al ref de useInView
        viewRef.current = node;
        // Asignar al ref externo
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            ref.current = node;
          }
        }
      }}
      className={`boat-card-fade-in${(inView || isFirstRow || hasBeenVisible) ? ' visible' : ''}`}
      style={{ willChange: 'opacity, transform' }}
      {...props}
    >
      {children}
    </div>
  );
});

const AllBoatsSection = () => {
  // Usar hook para sincronizar filtros y paginación con la URL
  const {
    filter,
    size,
    category,
    page,
    setFilter,
    setSize,
    setCategory,
    setPage,
  } = useBoatFiltersFromUrl();

  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const filterBySizeBtnRef = useRef(null);
  const filterByCategoryBtnRef = useRef(null);
  const lastBoatRef = useRef(null);
  const [categories, setCategories] = useState([]);

  // Cargar categorías al montar el componente (solo una vez)
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/categories/`);
        
        if (response.ok) {
          const data = await response.json();
          const categoriesWithBoats = data.filter(category => (category.boats_count || 0) > 0);
          setCategories(categoriesWithBoats);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Determinar qué filtro está activo (prioridad: Category > Size > Price/Featured)
  const getActiveFilter = () => {
    if (category) {
      return { type: 'category', value: { id: category } };
    }
    if (size) {
      return { type: 'size', value: { range: size } };
    }
    return { type: 'price', value: filter };
  };

  // Función para manejar cambios de filtro de precio (desactiva otros filtros)
  const handlePriceFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  // Función para manejar cambios de filtro de tamaño (desactiva otros filtros)
  const handleSizeFilterChange = (newSizeFilter) => {
    setSize(newSizeFilter ? newSizeFilter.range : null);
  };

  // Función para manejar cambios de filtro de categoría (desactiva otros filtros)
  const handleCategoryFilterChange = (newCategoryFilter) => {
    setCategory(newCategoryFilter ? newCategoryFilter.id : null);
  };

  // Buscar el objeto de categoría seleccionado a partir del id
  const selectedCategoryObj = categories.find(cat => String(cat.id) === String(category)) || null;

  // Intersection Observer para detectar cuando el usuario está cerca del final
  useEffect(() => {
    if (!hasMore || isFetchingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isFetchingMore) {
          setPage(page + 1);
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    if (lastBoatRef.current) {
      observer.observe(lastBoatRef.current);
    }

    return () => {
      if (lastBoatRef.current) {
        observer.unobserve(lastBoatRef.current);
      }
    };
  }, [boats, hasMore, isFetchingMore, lastBoatRef, setPage, page]);

  // Cargar botes con filtros aplicados
  useEffect(() => {
    setBoats([]);
    setHasMore(true);
    setLoading(true);
    setError(null);

    const activeFilter = getActiveFilter();
    
    BoatService.fetchBoats(1, activeFilter.type, activeFilter.value)
      .then((data) => {
        const newBoats = Array.isArray(data.results) ? data.results : [];
        
        setBoats(newBoats);
        setHasMore(Boolean(data.next));
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error loading boats:', error);
        console.error('Error details:', {
          message: error.message,
          status: error.status,
          stack: error.stack
        });
        setError('Failed to load boats.');
        setLoading(false);
      });
  }, [filter, size, category]);

  // Cargar páginas adicionales (no la primera)
  useEffect(() => {
    if (!hasMore || page === 1) return;
    setIsFetchingMore(true);
    const activeFilter = getActiveFilter();
    const loadPage = async () => {
      try {
        const data = await BoatService.fetchBoats(page, activeFilter.type, activeFilter.value);
        const newBoats = Array.isArray(data.results) ? data.results : [];
        setBoats(prev => [...prev, ...newBoats]);
        setHasMore(Boolean(data.next));
        setIsFetchingMore(false);
      } catch (error) {
        if (error.status === 404) {
          setHasMore(false);
          setIsFetchingMore(false);
        } else {
          setError('Failed to load boats.');
          setIsFetchingMore(false);
        }
      }
    };
    loadPage();
  }, [page]);

  // Forzar scroll al top al montar el componente, incluso si React Router restaura la posición
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="all-boats-section-bg">
      <div className="all-boats-section-container">
        <div className="all-boats-section-filter-row" style={{ position: 'relative' }}>
          <BoatListFilter 
            selected={filter} 
            onChange={handlePriceFilterChange} 
            onMoreFilterClick={() => setShowSizeModal(true)} 
            filterBySizeBtnRef={filterBySizeBtnRef} 
            sizeFilterActive={showSizeModal}
            sizeFilter={size} // <-- string, ej: '61-79'
            onCategoryFilterClick={() => setShowCategoryModal(true)}
            categoryFilter={selectedCategoryObj}
            showCategoryModal={showCategoryModal}
            filterByCategoryBtnRef={filterByCategoryBtnRef}
          />
          {showSizeModal && (
            <BoatSizeFilterModal
              onClose={() => setShowSizeModal(false)}
              onApply={handleSizeFilterChange}
              selected={size} // <-- string, ej: '61-79'
              anchorRef={filterBySizeBtnRef}
            />
          )}
          {showCategoryModal && (
            <BoatCategoryFilterModal
              onClose={() => setShowCategoryModal(false)}
              onApply={handleCategoryFilterChange}
              selected={selectedCategoryObj}
              anchorRef={filterByCategoryBtnRef}
              categories={categories}
            />
          )}
        </div>
        <div className="all-boats-section-grid">
          {error ? (
            <p>{error}</p>
          ) : boats.length > 0 ? (
            boats.map((boat, index) => {
              const isLastBoat = index === boats.length - 1;
              return (
                <BoatCardFadeInWrapper 
                  key={boat.id} 
                  index={index}
                  ref={isLastBoat && hasMore ? lastBoatRef : null}
                >
                  <BoatCard boat={boat} />
                </BoatCardFadeInWrapper>
              );
            })
          ) : loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              margin: '2em 0',
              flexDirection: 'column',
              gap: '1em'
            }}>
              <div className="spinner" style={{ 
                width: 40, 
                height: 40, 
                border: '4px solid rgba(62,194,237,0.2)', 
                borderTop: '4px solid #3EC2ED', 
                borderRadius: '50%'
              }} />
              <div style={{ 
                color: '#3EC2ED', 
                fontSize: '1.1rem', 
                fontWeight: '600',
                fontFamily: 'Montserrat, sans-serif'
              }}>
                Loading boats...
              </div>
            </div>
          ) : (
            <p>No boats available.</p>
          )}
        </div>
        {isFetchingMore && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            margin: '2em 0',
            flexDirection: 'column',
            gap: '1em'
          }}>
            <div className="spinner" style={{ 
              width: 40, 
              height: 40, 
              border: '4px solid rgba(62,194,237,0.2)', 
              borderTop: '4px solid #3EC2ED', 
              borderRadius: '50%'
            }} />
            <div style={{ 
              color: '#3EC2ED', 
              fontSize: '1.1rem', 
              fontWeight: '600',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              Loading more boats...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllBoatsSection;
